<template>
    <div id="layout-wrapper">
      <NavigationBar/>

      <!-- ============================================================== -->
      <!-- Start right Content here -->
      <!-- ============================================================== -->
      <div id="content" class="content-minimize">

        <div class="page-content">

          <div class="container-fluid">

            <div>
              <!-- Page header -->
              <div class="row mb-4">
                <div class="col-12">
                  <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h4 class="mb-sm-0 font-size-18">HR Events</h4>
                    <div class="page-title-right">
                      <ol class="breadcrumb m-0">
                        <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                        <li class="breadcrumb-item active">HR Management</li>
                        <li class="breadcrumb-item active">Events</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Filters and controls section -->
              <div class="row">
                <div class="col-12">
                  <div class="card">
                    <div class="card-body">
                      <div class="d-flex flex-wrap align-items-center mb-3">
                        <h5 class="card-title me-3 mb-3 mb-md-0">Event Filters</h5>
                        <div class="ms-auto">
                          <button @click="createEvent" class="btn btn-primary me-2">
                            <i class="mdi mdi-plus me-1"></i> Create Event
                          </button>
                          <button @click="refreshData" class="btn btn-secondary me-2">
                            <i class="mdi mdi-refresh me-1"></i> Refresh
                          </button>
                          <button @click="exportEvents" class="btn btn-success">
                            <i class="mdi mdi-file-export me-1"></i> Export
                          </button>
                        </div>
                      </div>

                      <div class="row g-3">
                        <div class="col-md-3">
                          <label class="form-label">Event Type</label>
                          <select v-model="filters.eventType" class="form-select">
                            <option value="">All Event Types</option>
                            <option v-for="type in eventTypes" :key="type" :value="type">
                              {{ type }}
                            </option>
                          </select>
                        </div>

                        <div class="col-md-3">
                          <label class="form-label">Department</label>
                          <select v-model="filters.departmentId" class="form-select">
                            <option value="">All Departments</option>
                            <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                              {{ dept.departmentName }}
                            </option>
                          </select>
                        </div>

                        <div class="col-md-3">
                          <label class="form-label">Date Range</label>
                          <div class="input-group">
                            <input v-model="filters.startDate" type="date" class="form-control">
                            <span class="input-group-text">to</span>
                            <input v-model="filters.endDate" type="date" class="form-control">
                          </div>
                        </div>

                        <div class="col-md-3">
                          <label class="form-label">Search</label>
                          <div class="position-relative">
                            <input
                                type="text"
                                v-model="filters.search"
                                class="form-control"
                                placeholder="Search by title, location..."
                            >
                            <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Statistics cards -->
              <div class="row">
                <!-- Total Events -->
                <div class="col-xl-3 col-md-6 mb-4">
                  <div class="card border-left-primary h-100 py-2">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="flex-shrink-0 me-3">
                          <div class="avatar-sm">
                          <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-check font-size-24"></i>
                          </span>
                          </div>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="font-size-15 mb-1">Total Events</h6>
                          <h4 class="mb-0">{{ metrics.totalEvents || 0 }}</h4>
                          <span class="text-muted font-size-12">
                          Past 30 days
                        </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Upcoming Events -->
                <div class="col-xl-3 col-md-6 mb-4">
                  <div class="card border-left-success h-100 py-2">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="flex-shrink-0 me-3">
                          <div class="avatar-sm">
                          <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-clock font-size-24"></i>
                          </span>
                          </div>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="font-size-15 mb-1">Upcoming Events</h6>
                          <h4 class="mb-0">{{ metrics.upcomingEvents || 0 }}</h4>
                          <span class="text-success font-size-12">Next 7 days</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Events This Month -->
                <div class="col-xl-3 col-md-6 mb-4">
                  <div class="card border-left-info h-100 py-2">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="flex-shrink-0 me-3">
                          <div class="avatar-sm">
                          <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-month font-size-24"></i>
                          </span>
                          </div>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="font-size-15 mb-1">This Month</h6>
                          <h4 class="mb-0">{{ metrics.eventsThisMonth || 0 }}</h4>
                          <span class="text-info font-size-12">{{ currentMonthName }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Today's Events -->
                <div class="col-xl-3 col-md-6 mb-4">
                  <div class="card border-left-warning h-100 py-2">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="flex-shrink-0 me-3">
                          <div class="avatar-sm">
                          <span class="avatar-title bg-warning rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-today font-size-24"></i>
                          </span>
                          </div>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="font-size-15 mb-1">Today's Events</h6>
                          <h4 class="mb-0">{{ metrics.todayEvents || 0 }}</h4>
                          <span class="text-warning font-size-12">{{ today }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Event Type Breakdown -->
              <div class="row">
                <div class="col-xl-6 col-lg-6 mb-4">
                  <div class="card h-100">
                    <div class="card-header bg-transparent">
                      <h5 class="card-title mb-0">Events by Type</h5>
                    </div>
                    <div class="card-body">
                      <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0" class="chart-container" style="position: relative; height:250px; width:100%">
                        <div class="table-responsive">
                          <table class="table table-sm table-bordered">
                            <thead class="table-light">
                            <tr>
                              <th>Event Type</th>
                              <th class="text-center">Count</th>
                              <th class="text-center">Percentage</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(count, type) in metrics.typeCounts" :key="type">
                              <td>{{ type }}</td>
                              <td class="text-center">{{ count }}</td>
                              <td class="text-center">
                                <div class="progress" style="height: 6px;">
                                  <div
                                      class="progress-bar"
                                      :class="getEventTypeColor(type)"
                                      role="progressbar"
                                      :style="`width: ${getEventTypePercentage(type)}%`"
                                      :aria-valuenow="getEventTypePercentage(type)"
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                  ></div>
                                </div>
                                {{ getEventTypePercentage(type) }}%
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div v-else class="text-center p-4">
                        <i class="mdi mdi-chart-bar font-size-48 text-muted"></i>
                        <p class="mt-2">No event type data available</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Upcoming Events List -->
                <div class="col-xl-6 col-lg-6 mb-4">
                  <div class="card h-100">
                    <div class="card-header bg-transparent">
                      <h5 class="card-title mb-0">Upcoming Events</h5>
                    </div>
                    <div class="card-body">
                      <div v-if="upcomingEvents.length > 0" class="upcoming-events-list">
                        <div v-for="event in upcomingEvents" :key="event.id" class="upcoming-event-item mb-3">
                          <div class="d-flex align-items-center p-3 border rounded">
                            <div class="event-date-badge me-3 text-center">
                              <span class="date-month">{{ formatDateMonth(event.startDate) }}</span>
                              <span class="date-day fs-4 fw-bold d-block">{{ formatDateDay(event.startDate) }}</span>
                            </div>
                            <div class="event-details flex-grow-1">
                              <h6 class="event-title mb-1">{{ event.title }}</h6>
                              <div class="event-meta text-muted small">
                                <span><i class="mdi mdi-clock-outline me-1"></i>{{ formatEventTime(event) }}</span>
                                <span class="ms-2"><i class="mdi mdi-map-marker me-1"></i>{{ event.location || 'N/A' }}</span>
                              </div>
                            </div>
                            <div class="event-type-badge ms-2">
                              <span class="badge rounded-pill" :class="getEventTypeColor(event.eventType)">{{ event.eventType }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-center p-4">
                        <i class="mdi mdi-calendar font-size-48 text-muted"></i>
                        <p class="mt-2">No upcoming events</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Events list table -->
              <div class="row">
                <div class="col-12">
                  <div class="card">
                    <div class="card-header bg-transparent">
                      <h5 class="card-title">Events List</h5>
                    </div>
                    <div class="card-body">
                      <!-- Loading overlay -->
                      <div v-if="loading" class="text-center p-5">
                        <div class="spinner-border text-primary me-2" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">Loading events data...</p>
                      </div>

                      <!-- Empty state -->
                      <div v-else-if="!events.length" class="text-center p-5">
                        <i class="mdi mdi-calendar-remove font-size-48 text-muted"></i>
                        <h5 class="mt-2">No events found</h5>
                        <p class="text-muted">Try adjusting your filters or create a new event</p>
                        <button @click="resetFilters" class="btn btn-sm btn-outline-primary mt-2">Reset Filters</button>
                      </div>

                      <!-- Events table -->
                      <div v-else class="table-responsive">
                        <table class="table table-hover table-bordered align-middle mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>Event</th>
                            <th>Type</th>
                            <th>Date & Time</th>
                            <th>Location</th>
                            <th>Department</th>
                            <th>Status</th>
                            <th class="text-center">Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="event in events" :key="event.id">
                            <td>
                              <div class="d-flex align-items-center">
                                <div class="avatar-xs me-2 bg-light rounded p-1 text-center">
                                  <i class="mdi mdi-calendar font-size-18 text-primary"></i>
                                </div>
                                <div>
                                  <h5 class="font-size-14 mb-0">{{ event.title }}</h5>
                                  <p class="text-muted mb-0 font-size-12">{{ event.eventCode }}</p>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span class="badge rounded-pill" :class="getEventTypeColor(event.eventType)">{{ event.eventType }}</span>
                            </td>
                            <td>
                              <div>
                                <span class="d-block"><i class="mdi mdi-calendar-range me-1"></i>{{ formatDate(event.startDate) }}</span>
                                <span v-if="event.startTime" class="d-block text-muted small">
                                  <i class="mdi mdi-clock-outline me-1"></i>{{ formatTime(event.startTime) }} - {{ formatTime(event.endTime) }}
                                </span>
                              </div>
                            </td>
                            <td>{{ event.location || 'N/A' }}</td>
                            <td>{{ event.departmentName || 'All Departments' }}</td>
                            <td>
                              <span class="badge" :class="getStatusBadgeClass(event.status)">
                                {{ event.status }}
                              </span>
                            </td>
                            <td class="text-center">
                              <div class="d-flex justify-content-center">
                                <button
                                    @click="viewEventDetails(event)"
                                    class="btn btn-sm btn-info me-2"
                                    title="View Details"
                                >
                                  <i class="mdi mdi-eye"></i>
                                </button>
                                <button
                                    @click="editEvent(event)"
                                    class="btn btn-sm btn-primary me-2"
                                    title="Edit Event"
                                >
                                  <i class="mdi mdi-pencil"></i>
                                </button>
                                <button
                                    v-if="event.status !== 'Canceled'"
                                    @click="cancelEvent(event)"
                                    class="btn btn-sm btn-danger"
                                    title="Cancel Event"
                                >
                                  <i class="mdi mdi-close"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Pagination -->
                      <div v-if="events.length" class="d-flex justify-content-between align-items-center mt-4">
                        <div>
                          Showing <span class="fw-medium">{{ events.length }}</span> of
                          <span class="fw-medium">{{ pagination.totalEvents }}</span> events
                        </div>
                        <nav v-if="pagination.totalPages > 1">
                          <ul class="pagination pagination-rounded mb-0">
                            <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                                <i class="mdi mdi-chevron-left"></i>
                              </a>
                            </li>
                            <li
                                v-for="page in getPageNumbers()"
                                :key="page"
                                class="page-item"
                                :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                            >
                              <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                                {{ page }}
                              </a>
                              <span v-else class="page-link">{{ page }}</span>
                            </li>
                            <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.totalPages }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                                <i class="mdi mdi-chevron-right"></i>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Event Create/Edit Modal -->
              <div class="modal fade" id="eventModal" tabindex="-1" aria-labelledby="eventModalLabel" aria-hidden="true">
                <div class="modal-dialog modal-lg">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title" id="eventModalLabel">{{ isEditMode ? 'Edit Event' : 'Create New Event' }}</h5>
                      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                      <form @submit.prevent="submitEvent">
                        <div class="row g-3">
                          <div class="col-md-12">
                            <label class="form-label">Event Title <span class="text-danger">*</span></label>
                            <input v-model="eventForm.title" type="text" class="form-control" required>
                          </div>

                          <div class="col-md-6">
                            <label class="form-label">Event Type <span class="text-danger">*</span></label>
                            <select v-model="eventForm.eventType" class="form-select" required>
                              <option value="" disabled>Select Event Type</option>
                              <option v-for="type in eventTypes" :key="type" :value="type">{{ type }}</option>
                            </select>
                          </div>

                          <div class="col-md-6">
                            <label class="form-label">Department</label>
                            <select v-model="eventForm.departmentId" class="form-select">
                              <option value="">All Departments</option>
                              <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                                {{ dept.departmentName }}
                              </option>
                            </select>
                          </div>

                          <div class="col-md-6">
                            <label class="form-label">Start Date <span class="text-danger">*</span></label>
                            <input v-model="eventForm.startDate" type="date" class="form-control" required>
                          </div>

                          <div class="col-md-6">
                            <label class="form-label">End Date</label>
                            <input v-model="eventForm.endDate" type="date" class="form-control">
                          </div>

                          <div class="col-md-6">
                            <label class="form-label">Start Time</label>
                            <input v-model="eventForm.startTime" type="time" class="form-control">
                          </div>

                          <div class="col-md-6">
                            <label class="form-label">End Time</label>
                            <input v-model="eventForm.endTime" type="time" class="form-control">
                          </div>

                          <div class="col-md-12">
                            <label class="form-label">Location</label>
                            <input v-model="eventForm.location" type="text" class="form-control">
                          </div>

                          <div class="col-md-12">
                            <label class="form-label">Virtual Meeting Link</label>
                            <input v-model="eventForm.virtualMeetingLink" type="text" class="form-control">
                          </div>

                          <div class="col-md-6">
                            <label class="form-label">Priority</label>
                            <select v-model="eventForm.priority" class="form-select">
                              <option value="Low">Low</option>
                              <option value="Medium">Medium</option>
                              <option value="High">High</option>
                            </select>
                          </div>

                          <div class="col-md-6">
                            <label class="form-label">Visibility</label>
                            <select v-model="eventForm.visibility" class="form-select">
                              <option value="Public">Public</option>
                              <option value="Department">Department Only</option>
                              <option value="Private">Private</option>
                            </select>
                          </div>

                          <div class="col-md-12">
                            <label class="form-label">Event Description</label>
                            <textarea v-model="eventForm.description" class="form-control" rows="3"></textarea>
                          </div>

                          <div class="col-md-12">
                            <label class="form-label">Attendees</label>
                            <select v-model="eventForm.attendees" class="form-select" multiple>
                              <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                                {{ employee.firstName }} {{ employee.lastName }} ({{ employee.departmentName }})
                              </option>
                            </select>
                            <small class="text-muted">Hold Ctrl/Cmd to select multiple employees</small>
                          </div>

                          <div class="col-md-12">
                            <label class="form-label">Attachments</label>
                            <input type="file" class="form-control" multiple>
                          </div>
                        </div>
                      </form>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                      <button type="button" class="btn btn-primary" @click="submitEvent">
                        {{ isEditMode ? 'Update Event' : 'Create Event' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Event Details Modal -->
              <div class="modal fade" id="eventDetailsModal" tabindex="-1" aria-labelledby="eventDetailsModalLabel" aria-hidden="true">
                <div class="modal-dialog modal-lg">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title" id="eventDetailsModalLabel">Event Details</h5>
                      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                      <div v-if="selectedEvent">
                        <div class="row mb-4">
                          <div class="col-md-8">
                            <h4 class="font-size-16">{{ selectedEvent.title }}</h4>
                            <p class="text-muted mb-1">
                            <span class="badge rounded-pill" :class="getEventTypeColor(selectedEvent.eventType)">
                              {{ selectedEvent.eventType }}
                            </span>
                            </p>
                            <p class="text-muted mb-0">Event Code: {{ selectedEvent.eventCode }}</p>
                          </div>
                          <div class="col-md-4 text-md-end">
                          <span class="badge" :class="getStatusBadgeClass(selectedEvent.status)">
                            {{ selectedEvent.status }}
                          </span>
                          </div>
                        </div>

                        <div class="row">
                          <div class="col-md-6">
                            <div class="card border">
                              <div class="card-header bg-light">
                                <h6 class="mb-0">Event Information</h6>
                              </div>
                              <div class="card-body">
                                <ul class="list-group list-group-flush">
                                  <li class="list-group-item">
                                    <div class="d-flex justify-content-between">
                                      <span><i class="mdi mdi-calendar me-2"></i>Date:</span>
                                      <span class="text-muted">{{ formatDate(selectedEvent.startDate) }}</span>
                                    </div>
                                  </li>
                                  <li class="list-group-item" v-if="selectedEvent.startTime">
                                    <div class="d-flex justify-content-between">
                                      <span><i class="mdi mdi-clock-outline me-2"></i>Time:</span>
                                      <span class="text-muted">{{ formatTime(selectedEvent.startTime) }} - {{ formatTime(selectedEvent.endTime) }}</span>
                                    </div>
                                  </li>
                                  <li class="list-group-item">
                                    <div class="d-flex justify-content-between">
                                      <span><i class="mdi mdi-map-marker me-2"></i>Location:</span>
                                      <span class="text-muted">{{ selectedEvent.location || 'N/A' }}</span>
                                    </div>
                                  </li>
                                  <li class="list-group-item" v-if="selectedEvent.virtualMeetingLink">
                                    <div class="d-flex justify-content-between">
                                      <span><i class="mdi mdi-video me-2"></i>Virtual Link:</span>
                                      <a :href="selectedEvent.virtualMeetingLink" target="_blank">Join Meeting</a>
                                    </div>
                                  </li>
                                  <li class="list-group-item">
                                    <div class="d-flex justify-content-between">
                                      <span><i class="mdi mdi-office-building me-2"></i>Department:</span>
                                      <span class="text-muted">{{ selectedEvent.departmentName || 'All Departments' }}</span>
                                    </div>
                                  </li>
                                  <li class="list-group-item">
                                    <div class="d-flex justify-content-between">
                                      <span><i class="mdi mdi-eye me-2"></i>Visibility:</span>
                                      <span class="text-muted">{{ selectedEvent.visibility }}</span>
                                    </div>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="card border h-100">
                              <div class="card-header bg-light">
                                <h6 class="mb-0">Description</h6>
                              </div>
                              <div class="card-body">
                                <p v-if="selectedEvent.description">{{ selectedEvent.description }}</p>
                                <p v-else class="text-muted text-center">No description provided</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="row mt-4">
                          <div class="col-md-12">
                            <div class="card border">
                              <div class="card-header bg-light">
                                <h6 class="mb-0">Attendees</h6>
                              </div>
                              <div class="card-body">
                                <div v-if="selectedEvent.attendeesList && selectedEvent.attendeesList.length > 0" class="table-responsive">
                                  <table class="table table-sm">
                                    <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Department</th>
                                      <th>Response</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(attendee, index) in selectedEvent.attendeesList" :key="index">
                                      <td>{{ attendee.name }}</td>
                                      <td>{{ attendee.department || 'N/A' }}</td>
                                      <td>
                                        <span class="badge rounded-pill"
                                              :class="getResponseBadgeClass(attendee.response)">
                                          {{ attendee.response || 'Pending' }}
                                        </span>
                                      </td>
                                    </tr>
                                    </tbody>
                                  </table>
                                </div>
                                <p v-else class="text-muted text-center">No attendees added to this event</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="row mt-4" v-if="selectedEvent.attachmentsList && selectedEvent.attachmentsList.length > 0">
                          <div class="col-md-12">
                            <div class="card border">
                              <div class="card-header bg-light">
                                <h6 class="mb-0">Attachments</h6>
                              </div>
                              <div class="card-body">
                                <div class="row">
                                  <div v-for="(attachment, index) in selectedEvent.attachmentsList" :key="index" class="col-md-4 mb-3">
                                    <div class="border rounded p-2">
                                      <div class="d-flex align-items-center">
                                        <i class="mdi mdi-file-document-outline font-size-24 me-2"></i>
                                        <div>
                                          <p class="mb-0 font-size-14 text-truncate">{{ attachment.originalName }}</p>
                                          <small class="text-muted">{{ formatFileSize(attachment.size) }}</small>
                                        </div>
                                      </div>
                                      <div class="text-end mt-2">
                                        <a :href="attachment.path" target="_blank" class="btn btn-sm btn-primary">
                                          <i class="mdi mdi-download me-1"></i>Download
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="row mt-4">
                          <div class="col-md-12">
                            <div class="card border">
                              <div class="card-header bg-light">
                                <h6 class="mb-0">Event History</h6>
                              </div>
                              <div class="card-body">
                                <ul class="timeline">
                                  <li class="timeline-item">
                                    <div class="timeline-marker bg-primary"></div>
                                    <div class="timeline-content">
                                      <h6 class="timeline-title">Event Created</h6>
                                      <p class="timeline-text mb-0">By: {{ selectedEvent.createdBy }}</p>
                                      <p class="timeline-text text-muted">{{ formatDateTime(selectedEvent.createdAt) }}</p>
                                    </div>
                                  </li>
                                  <li v-if="selectedEvent.updatedAt !== selectedEvent.createdAt" class="timeline-item">
                                    <div class="timeline-marker bg-info"></div>
                                    <div class="timeline-content">
                                      <h6 class="timeline-title">Event Updated</h6>
                                      <p class="timeline-text mb-0">By: {{ selectedEvent.updatedBy || selectedEvent.createdBy }}</p>
                                      <p class="timeline-text text-muted">{{ formatDateTime(selectedEvent.updatedAt) }}</p>
                                    </div>
                                  </li>
                                  <li v-if="selectedEvent.status === 'Canceled'" class="timeline-item">
                                    <div class="timeline-marker bg-danger"></div>
                                    <div class="timeline-content">
                                      <h6 class="timeline-title">Event Canceled</h6>
                                      <p class="timeline-text mb-0">By: {{ selectedEvent.canceledBy }}</p>
                                      <p class="timeline-text text-muted">{{ formatDateTime(selectedEvent.canceledAt) }}</p>
                                      <p v-if="selectedEvent.cancellationReason" class="timeline-text">
                                        Reason: {{ selectedEvent.cancellationReason }}
                                      </p>
                                    </div>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                      <button type="button" class="btn btn-primary" @click="editEvent(selectedEvent)">Edit Event</button>
                      <button
                          v-if="selectedEvent && selectedEvent.status !== 'Canceled'"
                          type="button"
                          class="btn btn-danger"
                          @click="cancelEvent(selectedEvent)"
                      >
                        Cancel Event
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Cancel Event Modal -->
              <div class="modal fade" id="cancelEventModal" tabindex="-1" aria-labelledby="cancelEventModalLabel" aria-hidden="true">
                <div class="modal-dialog">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title" id="cancelEventModalLabel">Cancel Event</h5>
                      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                      <div v-if="selectedEvent">
                        <p>Are you sure you want to cancel the event: <strong>{{ selectedEvent.title }}</strong>?</p>
                        <div class="mb-3">
                          <label class="form-label">Reason for Cancellation</label>
                          <textarea v-model="cancellationReason" class="form-control" rows="3" placeholder="Enter reason for cancellation"></textarea>
                        </div>
                        <div class="form-check mb-3">
                          <input class="form-check-input" type="checkbox" v-model="notifyAttendees" id="notifyAttendees">
                          <label class="form-check-label" for="notifyAttendees">
                            Notify all attendees about cancellation
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                      <button type="button" class="btn btn-danger" @click="confirmCancelEvent">Confirm Cancellation</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>

      </div>

    </div>
  </template>


<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import NavigationBar from "@/components/Navigation.vue";

export default {
  name: "HREvents",
  components: {NavigationBar},
  data() {
    return {
      loading: false,
      events: [],
      upcomingEvents: [],
      departments: [],
      employees: [],
      eventTypes: ['Meeting', 'Training', 'Holiday', 'Deadline', 'Conference', 'Team Building', 'Announcement', 'Other'],
      filters: {
        departmentId: "",
        eventType: "",
        startDate: this.getDateOffset(-30), // Last 30 days
        endDate: this.getDateOffset(30), // Next 30 days
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalEvents: 0,
        limit: 10
      },
      metrics: {
        totalEvents: 0,
        upcomingEvents: 0,
        eventsThisMonth: 0,
        todayEvents: 0,
        typeCounts: {}
      },
      selectedEvent: null,
      eventForm: {
        title: "",
        description: "",
        eventType: "",
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        location: "",
        virtualMeetingLink: "",
        departmentId: "",
        attendees: [],
        isAllDay: false,
        priority: "Medium",
        visibility: "Public"
      },
      isEditMode: false,
      cancellationReason: "",
      notifyAttendees: true
    };
  },
  computed: {
    today() {
      return new Date().toLocaleDateString();
    },
    currentMonthName() {
      return new Date().toLocaleString('default', { month: 'long' });
    }
  },
  mounted() {
    this.getDepartments();
    this.getEmployees();
    this.getEvents();
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
              this.showToast("error", "Failed to load employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employees");
          });
    },

    getEvents() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getEvents");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.eventType) {
        data.append("eventType", this.filters.eventType);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.events = res.data.data.events || [];
              this.metrics = res.data.data.metrics || {};
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalEvents: 0,
                limit: 10
              };

              // Get upcoming events (next 7 days)
              const today = new Date();
              const nextWeek = new Date();
              nextWeek.setDate(today.getDate() + 7);

              this.upcomingEvents = this.events
                  .filter(event => {
                    const eventDate = new Date(event.startDate);
                    return eventDate >= today && eventDate <= nextWeek && event.status !== 'Canceled';
                  })
                  .slice(0, 5); // Top 5 upcoming events
            } else {
              console.error("Error fetching events:", res.data.message);
              this.showToast("error", "Failed to load events data: " + res.data.message);
              this.events = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching events");
            this.events = [];
            this.metrics = {};
          });
    },

    getEventDetails(eventId) {
      const data = new FormData();
      data.append("function", "getEventDetails");
      data.append("eventId", eventId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedEvent = res.data.data.event;
              // Open the event details modal
              new bootstrap.Modal(document.getElementById('eventDetailsModal')).show();
            } else {
              console.error("Error fetching event details:", res.data.message);
              this.showToast("error", "Failed to load event details: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching event details");
          });
    },

    refreshData() {
      this.getEvents();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        eventType: "",
        startDate: this.getDateOffset(-30),
        endDate: this.getDateOffset(30),
        search: "",
        page: 1,
        limit: 10
      };
      this.getEvents();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getEvents();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    createEvent() {
      this.isEditMode = false;
      this.eventForm = {
        title: "",
        description: "",
        eventType: "",
        startDate: new Date().toISOString().split('T')[0], // Today's date
        endDate: "",
        startTime: "",
        endTime: "",
        location: "",
        virtualMeetingLink: "",
        departmentId: "",
        attendees: [],
        isAllDay: false,
        priority: "Medium",
        visibility: "Public"
      };
      new bootstrap.Modal(document.getElementById('eventModal')).show();
    },

    editEvent(event) {
      this.isEditMode = true;
      this.selectedEvent = event;

      // Close details modal if open
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('eventDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Prepare form data
      this.eventForm = {
        id: event.id,
        title: event.title,
        description: event.description || "",
        eventType: event.eventType,
        startDate: event.startDate,
        endDate: event.endDate || "",
        startTime: event.startTime || "",
        endTime: event.endTime || "",
        location: event.location || "",
        virtualMeetingLink: event.virtualMeetingLink || "",
        departmentId: event.departmentId || "",
        attendees: event.attendeesList ? event.attendeesList.map(att => att.employeeId) : [],
        isAllDay: event.isAllDay === 1,
        priority: event.priority || "Medium",
        visibility: event.visibility || "Public"
      };

      // Open the event modal
      new bootstrap.Modal(document.getElementById('eventModal')).show();
    },

    submitEvent() {
      // Validate required fields
      if (!this.eventForm.title || !this.eventForm.startDate || !this.eventForm.eventType) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      // Prepare form data
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateEvent");
        data.append("eventId", this.eventForm.id);
      } else {
        data.append("function", "createEvent");
      }

      // Add all form fields
      data.append("title", this.eventForm.title);
      data.append("description", this.eventForm.description);
      data.append("eventType", this.eventForm.eventType);
      data.append("startDate", this.eventForm.startDate);
      data.append("endDate", this.eventForm.endDate);
      data.append("startTime", this.eventForm.startTime);
      data.append("endTime", this.eventForm.endTime);
      data.append("location", this.eventForm.location);
      data.append("virtualMeetingLink", this.eventForm.virtualMeetingLink);
      data.append("departmentId", this.eventForm.departmentId);
      data.append("isAllDay", this.eventForm.isAllDay ? 1 : 0);
      data.append("priority", this.eventForm.priority);
      data.append("visibility", this.eventForm.visibility);

      // Handle attendees
      if (this.eventForm.attendees.length > 0) {
        // Create attendees array of objects
        const attendeesArray = this.eventForm.attendees.map(empId => {
          const employee = this.employees.find(emp => emp.id === empId);
          return {
            employeeId: empId,
            name: employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown'
          };
        });
        data.append("attendees", JSON.stringify(attendeesArray));
      }

      // Send the request
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", this.isEditMode ? "Event updated successfully" : "Event created successfully");

              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('eventModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the events data
              this.getEvents();
            } else {
              console.error("Error saving event:", res.data.message);
              this.showToast("error", `Failed to ${this.isEditMode ? 'update' : 'create'} event: ${res.data.message}`);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", `Network error when ${this.isEditMode ? 'updating' : 'creating'} event`);
          });
    },

    viewEventDetails(event) {
      this.selectedEvent = null;
      this.getEventDetails(event.id);
    },

    cancelEvent(event) {
      this.selectedEvent = event;
      this.cancellationReason = "";
      this.notifyAttendees = true;

      // Close any other open modals
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('eventDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the cancel event modal
      new bootstrap.Modal(document.getElementById('cancelEventModal')).show();
    },

    confirmCancelEvent() {
      if (!this.selectedEvent) return;

      const data = new FormData();
      data.append("function", "cancelEvent");
      data.append("eventId", this.selectedEvent.id);
      data.append("reason", this.cancellationReason);
      data.append("notifyAttendees", this.notifyAttendees ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Event canceled successfully");

              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('cancelEventModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the events data
              this.getEvents();
            } else {
              console.error("Error canceling event:", res.data.message);
              this.showToast("error", "Failed to cancel event: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when canceling event");
          });
    },

    exportEvents() {
      const data = new FormData();
      data.append("function", "exportEventsReport");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.eventType) {
        data.append("eventType", this.filters.eventType);
      }

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    // Helper methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatTime(timeString) {
      if (!timeString) return '';

      // If the time string is already in 12-hour format
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }

      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = timeString.split(':');
      let hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12;
      hour = hour ? hour : 12; // the hour '0' should be '12'
      return `${hour}:${minutes} ${ampm}`;
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    formatEventTime(event) {
      if (event.isAllDay) return 'All Day';
      if (!event.startTime) return 'No Time Specified';

      return `${this.formatTime(event.startTime)}${event.endTime ? ' - ' + this.formatTime(event.endTime) : ''}`;
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getEventTypeColor(type) {
      if (!type) return 'bg-secondary';

      const typeMap = {
        'Meeting': 'bg-success',
        'Training': 'bg-purple',
        'Holiday': 'bg-warning',
        'Deadline': 'bg-danger',
        'Conference': 'bg-info',
        'Team Building': 'bg-success',
        'Announcement': 'bg-warning',
        'Other': 'bg-secondary'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';

      const statusMap = {
        'Scheduled': 'bg-success',
        'Canceled': 'bg-danger',
        'Completed': 'bg-info',
        'In Progress': 'bg-warning',
        'Postponed': 'bg-warning'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getResponseBadgeClass(response) {
      if (!response) return 'bg-secondary';

      const responseMap = {
        'Accepted': 'bg-success',
        'Declined': 'bg-danger',
        'Tentative': 'bg-warning',
        'Pending': 'bg-secondary'
      };

      return responseMap[response] || 'bg-secondary';
    },

    getEventTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalEvents) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      return ((count / this.metrics.totalEvents) * 100).toFixed(1);
    },

    showToast(type, message) {
      // Check if toast plugin exists before using it
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback to alert if toast plugin not available
        alert(message);
      }
    }
  },
  watch: {
    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 300),

    'filters.eventType': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 300),

    'filters.startDate': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 300),

    'filters.endDate': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 500)
  }
};
</script>


<style scoped>
.avatar-xs {
  height: 2rem;
  width: 2rem;
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.event-date-badge {
  min-width: 60px;
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 8px;
}

.date-month {
  text-transform: uppercase;
  font-size: 12px;
  color: #6c757d;
}

.timeline {
  list-style-type: none;
  position: relative;
  padding-left: 30px;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-marker {
  position: absolute;
  left: -30px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  top: 0;
}

.timeline-content {
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
}

.timeline-title {
  margin-bottom: 5px;
}

.timeline-text {
  line-height: 1.4;
}

/* Event type colors */
.bg-meeting {
  background-color: #28a745 !important;
}

.bg-training {
  background-color: #6f42c1 !important;
}

.bg-holiday {
  background-color: #fd7e14 !important;
}

.bg-deadline {
  background-color: #dc3545 !important;
}

.bg-conference {
  background-color: #17a2b8 !important;
}

.bg-teambuilding {
  background-color: #20c997 !important;
}

.bg-announcement {
  background-color: #ffc107 !important;
}

.bg-other {
  background-color: #6c757d !important;
}
</style>